import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Edit2 } from 'lucide-react';
import { updateBusiness4FlyoTicket } from '../../services/api';

const ticketClassOptions = [
  { value: 'economy', label: 'Economy' },
  { value: 'premium economy', label: 'Premium Economy' },
  { value: 'business class', label: 'Business Class' },
  { value: 'first class', label: 'First Class' },
];

const ticketStatusOptions = [
  { value: 'confirmed', label: 'Confirmed', optionLabel: '?? Confirmed', badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'pending', label: 'Pending', optionLabel: '?? Pending', badgeClass: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'cancelled', label: 'Cancelled', optionLabel: '?? Cancelled', badgeClass: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'refunded', label: 'Refunded', optionLabel: '?? Refunded', badgeClass: 'bg-sky-50 text-sky-700 border-sky-200' },
];

const refundableOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const currencyOptions = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'INR', label: 'INR' },
  { value: 'AED', label: 'AED' },
  { value: 'SGD', label: 'SGD' },
  { value: 'AUD', label: 'AUD' },
  { value: 'CAD', label: 'CAD' },
  { value: 'CHF', label: 'CHF' },
  { value: 'JPY', label: 'JPY' },
  { value: 'CNY', label: 'CNY' },
  { value: 'IDR', label: 'IDR' },
  { value: 'THB', label: 'THB' },
  { value: 'MYR', label: 'MYR' },
  { value: 'PHP', label: 'PHP' },
];

const normalizeText = (value) => (value ?? '').toString().trim();

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getTicketStatusBadgeClass = (statusValue) => {
  const statusOption = ticketStatusOptions.find((option) => option.value === statusValue);
  return statusOption?.badgeClass || 'bg-gray-50 text-gray-700 border-gray-200';
};

const FieldCard = ({
  label,
  value,
  displayValueOverride,
  onChange,
  isEditing,
  onEnableEdit,
  type = 'text',
  options = null,
  displayBadgeClass = '',
}) => {
  const displayValue = displayValueOverride ?? (value === '' || value === null || value === undefined ? 'N/A' : value);

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-3 transition hover:border-blue-300"
      onClick={!isEditing ? onEnableEdit : undefined}
    >
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</label>
        {!isEditing && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onEnableEdit();
            }}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title={`Edit ${label}`}
          >
            <Edit2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isEditing ? (
        options ? (
          <select
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
          >
            <option value="">Select {label}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.optionLabel || option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
            placeholder={`Enter ${label}`}
          />
        )
      ) : displayBadgeClass ? (
        <span className={`inline-flex rounded-full border px-2.5 py-1 text-sm font-semibold ${displayBadgeClass}`}>
          {displayValue}
        </span>
      ) : (
        <p className="text-sm font-semibold text-gray-800">{displayValue}</p>
      )}
    </div>
  );
};

const ReadOnlyCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
    <div className="mb-2 flex items-center gap-2">
      <Icon className="h-4 w-4 text-gray-500" />
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    </div>
    <p className="text-sm font-semibold text-gray-800">{value || 'N/A'}</p>
  </div>
);

/**
 * Ticket Info Tab Component for Business4Flyo
 * @param {Object} props - Component props
 * @param {Object} props.ticketData - Ticket data object
 * @param {Object} props.amount - Amount object
 * @param {Array} props.serviceAmount - Array of service amounts
 * @param {number} props.ticketId - Ticket ID from URL
 * @param {number} props.agentId - Agent ID from ticket data
 * @param {Function} props.onUpdate - Callback to refresh data after update
 * @returns {JSX.Element} B4FTicketInfoTab component
 */
const B4FTicketInfoTab = ({ ticketData, amount, serviceAmount, ticketId, agentId, onUpdate }) => {
  const [formValues, setFormValues] = useState({});
  const [editableFields, setEditableFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');

  const initialValues = useMemo(() => ({
    ticketReference: ticketData?.ticketReference || ticketData?.bookingReference || '',
    pnr: ticketData?.pnr || '',
    ticketClass: (ticketData?.ticketClass || ticketData?.bookingClass || '').toLowerCase(),
    ticketStatus: (ticketData?.ticketStatus || ticketData?.bookingStatus || '').toLowerCase(),
    refundable: ticketData?.isRefundable === true ? 'true' : ticketData?.isRefundable === false ? 'false' : '',
    currency: amount?.currency || '',
    base_fare: amount?.base_fare ?? '',
    tax: amount?.tax ?? '',
    total_amount: amount?.total_amount ?? amount?.price ?? '',
  }), [ticketData, amount]);

  useEffect(() => {
    setFormValues(initialValues);
    setEditableFields({});
    setSaveError('');
  }, [initialValues]);

  const ticketDate = ticketData?.ticketDate || ticketData?.bookingDate;
  const ticketSavedAt = ticketData?.ticketSavedAt || ticketData?.bookingSavedAt;

  const pendingPayload = useMemo(() => {
    const payload = {};

    const textFields = ['ticketReference', 'pnr', 'ticketClass', 'ticketStatus'];
    textFields.forEach((field) => {
      const currentValue = normalizeText(formValues[field]);
      const initialValue = normalizeText(initialValues[field]);
      if (currentValue !== initialValue) {
        payload[field] = currentValue;
      }
    });

    const initialRefundable = initialValues.refundable === '' ? '' : initialValues.refundable === 'true';
    const currentRefundable = formValues.refundable === '' ? '' : formValues.refundable === 'true';
    if (currentRefundable !== initialRefundable && currentRefundable !== '') {
      payload.refundable = currentRefundable;
    }

    const amountPatch = {};

    const currentCurrency = normalizeText(formValues.currency);
    const initialCurrency = normalizeText(initialValues.currency);
    if (currentCurrency !== initialCurrency) {
      amountPatch.currency = currentCurrency;
    }

    ['base_fare', 'tax', 'total_amount'].forEach((field) => {
      const currentValue = toNumberOrNull(formValues[field]);
      const initialValue = toNumberOrNull(initialValues[field]);
      if (currentValue !== initialValue) {
        amountPatch[field] = currentValue;
      }
    });

    if (Object.keys(amountPatch).length > 0) {
      payload.amount = amountPatch;
    }

    return payload;
  }, [formValues, initialValues]);

  const hasChanges = Object.keys(pendingPayload).length > 0;

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const enableField = (field) => {
    setEditableFields((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleSaveChanges = async () => {
    if (!hasChanges) return;

    if (!agentId || !ticketId) {
      alert('Missing agentId or ticketId');
      return;
    }

    try {
      setLoading(true);
      setSaveError('');

      await updateBusiness4FlyoTicket({
        agentId: Number(agentId),
        ticketId: Number(ticketId),
        ...pendingPayload,
      });

      setEditableFields({});
      if (onUpdate) onUpdate();
    } catch (error) {
      const message = error?.message || 'Failed to update ticket details';
      setSaveError(message);
      alert(`Error updating ticket: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const hasServiceAmount = serviceAmount && serviceAmount.length > 0;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-gray-900">Ticket Information</h2>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <FieldCard
            label="Agent Booking ID"
            value={formValues.ticketReference}
            onChange={(value) => handleFieldChange('ticketReference', value)}
            isEditing={!!editableFields.ticketReference}
            onEnableEdit={() => enableField('ticketReference')}
          />

          <FieldCard
            label="PNR"
            value={formValues.pnr}
            onChange={(value) => handleFieldChange('pnr', value)}
            isEditing={!!editableFields.pnr}
            onEnableEdit={() => enableField('pnr')}
          />

          <ReadOnlyCard icon={Calendar} label="Ticket Date" value={ticketDate || 'N/A'} />

          <FieldCard
            label="Class"
            value={formValues.ticketClass}
            displayValueOverride={
              ticketClassOptions.find((option) => option.value === formValues.ticketClass)?.label ||
              (formValues.ticketClass ? formValues.ticketClass : 'N/A')
            }
            onChange={(value) => handleFieldChange('ticketClass', value)}
            isEditing={!!editableFields.ticketClass}
            onEnableEdit={() => enableField('ticketClass')}
            options={ticketClassOptions}
          />

          <FieldCard
            label="Ticket Status"
            value={formValues.ticketStatus}
            displayValueOverride={
              ticketStatusOptions.find((option) => option.value === formValues.ticketStatus)?.label ||
              (formValues.ticketStatus ? formValues.ticketStatus : 'N/A')
            }
            onChange={(value) => handleFieldChange('ticketStatus', value)}
            isEditing={!!editableFields.ticketStatus}
            onEnableEdit={() => enableField('ticketStatus')}
            options={ticketStatusOptions}
            displayBadgeClass={getTicketStatusBadgeClass(formValues.ticketStatus)}
          />

          <FieldCard
            label="Refundable"
            value={formValues.refundable}
            displayValueOverride={formValues.refundable === 'true' ? 'Yes' : formValues.refundable === 'false' ? 'No' : 'N/A'}
            onChange={(value) => handleFieldChange('refundable', value)}
            isEditing={!!editableFields.refundable}
            onEnableEdit={() => enableField('refundable')}
            options={refundableOptions}
          />

          <ReadOnlyCard icon={Calendar} label="Ticket Saved At" value={ticketSavedAt || 'N/A'} />

          <ReadOnlyCard icon={Calendar} label="Trip Type" value={ticketData?.tripType || 'N/A'} />
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-gray-900">Fare Information</h2>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <FieldCard
            label="Currency"
            value={formValues.currency}
            onChange={(value) => handleFieldChange('currency', value)}
            isEditing={!!editableFields.currency}
            onEnableEdit={() => enableField('currency')}
            options={currencyOptions}
          />

          <FieldCard
            label="Base Fare"
            value={formValues.base_fare}
            onChange={(value) => handleFieldChange('base_fare', value)}
            isEditing={!!editableFields.base_fare}
            onEnableEdit={() => enableField('base_fare')}
            type="number"
          />

          <FieldCard
            label="Taxes & Fees"
            value={formValues.tax}
            onChange={(value) => handleFieldChange('tax', value)}
            isEditing={!!editableFields.tax}
            onEnableEdit={() => enableField('tax')}
            type="number"
          />

          <FieldCard
            label="Total Fare"
            value={formValues.total_amount}
            onChange={(value) => handleFieldChange('total_amount', value)}
            isEditing={!!editableFields.total_amount}
            onEnableEdit={() => enableField('total_amount')}
            type="number"
          />
        </div>

        {hasChanges && (
          <div className="mt-5 flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={() => {
                setFormValues(initialValues);
                setEditableFields({});
                setSaveError('');
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={loading}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

        {saveError && (
          <p className="mt-3 text-sm text-red-600">{saveError}</p>
        )}
      </div>

      {hasServiceAmount && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900">Service Amounts</h2>
          <div className="mt-4 overflow-x-auto rounded-xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Service</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Currency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {serviceAmount.map((service, index) => (
                  <tr key={`${service.serviceName || 'service'}-${index}`}>
                    <td className="px-4 py-3 text-gray-800">{service.serviceName || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-800">
                      {service.amount?.price !== undefined && service.amount?.price !== null
                        ? service.amount.price
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-gray-800">{service.amount?.currency || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default B4FTicketInfoTab;
